import type { Theme, SxProps, Breakpoint } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { CONFIG } from 'src/config-global';

import { Logo } from 'src/components/logo';

import { alpha, useTheme, useColorScheme } from '@mui/material/styles';
import { allLangs } from 'src/locales';
import { LanguagePopover } from 'src/layouts/components/language-popover';
import { useSettingsContext } from 'src/components/settings';
import { IconButton, Stack, Typography } from '@mui/material';
import { red } from '@mui/material/colors';
import { Iconify } from 'src/components/iconify';
import ParkCityLogo from 'src/components/logo/logo_new';
import { Section } from './section';
import { Main, Content } from './main';
import { HeaderSection } from '../core/header-section';
import { LayoutSection } from '../core/layout-section';
import { SettingsButton } from '../components/settings-button';
import { useAuthContext } from 'src/context/AuthContext';

// ----------------------------------------------------------------------

export type AuthSplitLayoutProps = {
  sx?: SxProps<Theme>;
  children: React.ReactNode;
  header?: {
    sx?: SxProps<Theme>;
    slots?: {
      leftArea?: React.ReactNode;
      rightArea?: React.ReactNode;
      topArea?: React.ReactNode;
      centerArea?: React.ReactNode;
      bottomArea?: React.ReactNode;
    };
  };
  section?: {
    title?: string;
    imgUrl?: string | boolean;
    subtitle?: string;
    bgImage?: {
      dark?: string;
      light?: string;
    };
    customSection?: any;
    renderBackButton?: boolean;
    backButtonPage?:string;
    showLogout?: boolean;
  };
};

export function AuthSplitLayout({ sx, section, children, header }: AuthSplitLayoutProps) {
  const layoutQuery: Breakpoint = 'md';

  const theme = useTheme();

  const settings = useSettingsContext();

  const { mode, setMode } = useColorScheme();



  return (
    <LayoutSection
      headerSection={
        /** **************************************
         * Header
         *************************************** */
        <HeaderSection
          disableElevation
          layoutQuery={layoutQuery}
          slotProps={{ container: { maxWidth: false } }}
          sx={{ position: { [layoutQuery]: 'fixed' }, ...header?.sx, width: '75vw' }}
          slots={{
            topArea: header?.slots?.topArea ?? (
              <Alert severity="info" sx={{ display: 'none', borderRadius: 0 }}>
                This is an info Alert.
              </Alert>
            ),
            leftArea: header?.slots?.leftArea ?? (
              <>
                {/* -- Logo -- */}
                {/* <Box
                  component={RouterLink as React.ElementType}
                  to={logoTarget}
                  sx={{
                    height: '3rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    textDecoration: 'none',
                  }}
                >
                  <ParkCityLogo />
                </Box> */}
              </>
            ),
            centerArea: header?.slots?.centerArea ?? <></>,
            rightArea: header?.slots?.rightArea ?? (
              <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 1.5 }}>
                {/* -- Help link -- */}
                <Link
                  target="_blank"
                  href={paths.faqs}
                  component={RouterLink}
                  color="inherit"
                  sx={{ typography: 'subtitle2' }}
                >
                  Need help?
                </Link>
                {/* -- Settings button -- */}
                <LanguagePopover data={allLangs} />
                <IconButton
                  onClick={() => {
                    settings.onUpdateField('colorScheme', mode === 'light' ? 'dark' : 'light');
                    setMode(mode === 'light' ? 'dark' : 'light');
                  }}
                >
                  <Iconify
                    icon={
                      mode === 'dark'
                        ? 'line-md:moon-filled-to-sunny-filled-loop-transition'
                        : 'line-md:moon-rising-filled-loop'
                    }
                    height={48}
                    color={mode === 'dark' ? red[400] : '-moz-initial'}
                  />
                </IconButton>
              </Box>
            ),
          }}
        />
      }
      /** **************************************
       * Footer
       *************************************** */
      footerSection={null}
      /** **************************************
       * Style
       *************************************** */
      cssVars={{ '--layout-auth-content-width': '420px' }}
      sx={sx}
    >
      <Main layoutQuery={layoutQuery}>
        <Section
          title={section?.title}
          layoutQuery={layoutQuery}
          imgUrl={section?.imgUrl}
          subtitle={section?.subtitle}
          bgImage={theme.palette.mode === 'dark' ? section?.bgImage?.dark : section?.bgImage?.light}
          customSection={section?.customSection}
          renderBackButton={section?.renderBackButton}
          backButtonPage={section?.backButtonPage}
          showLogout={section?.showLogout}
        />
        <Content layoutQuery={layoutQuery}>{children}</Content>
      </Main>
    </LayoutSection>
  );
}
